import { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, GiftIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { LoadFailed } from '../ui/LoadState';
import { Badge } from '../ui/Badge';
import { Skeleton } from '../../ui/Skeleton';
import { btnPrimary, btnSecondary, fadeWhileLoading, inputCls, listBox, rowLink } from '../ui/styles';
import { money } from '../../core/format/currency';
import { sourceLabel, statusOptions, untilLabel, voucherStatusBadge } from './voucherLabels';
import { useVouchers } from './useVouchers';
import { plural } from '../../core/format/text';
import { API_BASE } from '../../core/api/http';

/** Alle cadeaubonnen: zoeken op code of de laatste vier, filteren op status. Openstaand bovenaan. */
export function Vouchers() {
  const {
    query,
    page,
    status,
    reload,
    setFilter,
    clearFilters,
    hasFilters,
    search,
    setSearch
  } = useVouchers();
  const {
    search: listQuery
  } = useLocation();
  const listRef = useRef<HTMLUListElement>(null);
  const goToPage = (p: number) => {
    setFilter('pagina', String(p));
    listRef.current?.scrollIntoView({
      block: 'start'
    });
  };
  return <div data-id="element-5441" data-mp="3qfbl4xi6iyjh">
      <PageHeader title="Cadeaubonnen" description="Elke bon met zijn eigen code, saldo en einddatum. Deels inwisselen kan; het restant blijft op de bon." actions={<Link to="/be/cadeaubonnen/nieuw" className={btnPrimary} data-id="element-5443" data-mp="2xrp4anjxxxeo">
            <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5444" data-mp="32q0e4ya8k7ww" />
            Nieuwe bon
          </Link>} data-id="element-5442" data-mp="3qgmnqtzw2090" />

      {/* Het getal waar Sarah voor komt: wat er nog uitgegeven kan worden. Los van de filters. */}
      <div className="mb-6 rounded-lg border border-line bg-white p-4 sm:p-6" data-id="element-5445" data-mp="3bnj3ufytraf4">
        <p className="text-[13px] text-muted" data-id="element-5446" data-mp="mx9tf3t74t2e">Openstaand op actieve bonnen</p>
        {page ? <p className="mt-1 font-display text-[2rem] font-light leading-tight tabular-nums text-ink" data-id="element-5447" data-mp="qxtq669uxuo0">{money(page.outstandingCents)}</p> : status === 'error' ? <p className="mt-1 text-[14px] text-muted" data-id="element-5448" data-mp="2j1r0kbmxtcwg">Kon niet laden.</p> : <Skeleton className="mt-2 h-9 w-32 rounded" data-id="element-5449" data-mp="2x18x5d6h5ugd" />}
        <p className="mt-1 text-[13px] text-muted" data-id="element-5450" data-mp="2wymmh4oaeytx">Nog uit te geven, dus geen omzet. Het telt pas mee als de bon wordt ingewisseld.</p>
      </div>

      <form role="search" onSubmit={e => e.preventDefault()} className="mb-5 grid gap-3 sm:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]" data-id="element-5451" data-mp="24ba4l7t2nf6y">
        <label className="relative block" data-id="element-5452" data-mp="20cayvkzineeg">
          <span className="sr-only" data-id="element-5453" data-mp="2e2egtzrd4ly3">Zoek op code, de laatste vier tekens of de naam van de koper</span>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-5454" data-mp="29ammde535w2o" />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Escape' && search && setSearch('')} placeholder="Code, laatste vier of koper" autoCapitalize="characters" spellCheck={false} className={`${inputCls} pl-9`} data-id="element-5455" data-mp="1ljqxopvxfgzn" />
        </label>
        <label className="block" data-id="element-5456" data-mp="3jaz3acgo7uvz">
          <span className="sr-only" data-id="element-5457" data-mp="3o7zbasx2z7pq">Status</span>
          <select value={query.status ?? ''} onChange={e => setFilter('status', e.target.value || undefined)} className={inputCls} data-id="element-5458" data-mp="3o8qvl7l89j0p">
            <option value="" data-id="element-5459" data-mp="ju0vphgrq7lx">Alle statussen</option>
            {statusOptions.map(o => <option key={o.value} value={o.value} data-id="element-5460" data-mp="jrofb8qns5az">
                {o.label}
              </option>)}
          </select>
        </label>
      </form>

      <p className="mb-3 flex min-h-[1.5rem] flex-wrap items-center justify-between gap-2 text-[13px] text-muted" role="status" aria-live="polite" data-id="element-5461" data-mp="as1gmupbddlm">
        <span data-id="element-5462" data-mp="2vjjwo7hv2rex">{status === 'loading' ? 'Cadeaubonnen laden…' : page ? plural(page.total, 'cadeaubon', 'cadeaubonnen') : ''}</span>
        {hasFilters && <button type="button" onClick={clearFilters} className="rounded-md px-1 text-ink underline underline-offset-4 hover:text-clay" data-id="element-5463" data-mp="3aurp1o9ur10w">
            Filters wissen
          </button>}
      </p>

      {status === 'error' && !page ? <LoadFailed title="De cadeaubonnen konden niet laden" onRetry={reload} data-id="element-5464" data-mp="2x2247n62825d" /> : status === 'loading' && !page ? <ul className={listBox} aria-hidden="true" data-id="element-5465" data-mp="3tscht6fqmjqs">
          {Array.from({
        length: 6
      }, (_, i) => <li key={i} className="flex items-center gap-4 px-4 py-4 sm:px-5" data-id="element-5466" data-mp="3pritibydv2xd">
              <Skeleton className="h-5 w-16 rounded-full" data-id="element-5467" data-mp="3kqv39shzaq47" />
              <span className="flex-1 space-y-1.5" data-id="element-5468" data-mp="3kthb7t8s77my">
                <Skeleton className="h-3.5 w-32 rounded" data-id="element-5469" data-mp="3ft2zo17z1fk6" />
                <Skeleton className="h-3 w-48 rounded" data-id="element-5470" data-mp="3funt1nijusgg" />
              </span>
              <Skeleton className="h-4 w-16 rounded" data-id="element-5471" data-mp="29dixvd6klzfx" />
            </li>)}
        </ul> : page && page.items.length === 0 ? <EmptyState icon={GiftIcon} title={hasFilters ? 'Geen cadeaubonnen gevonden' : 'Nog geen cadeaubonnen'} text={hasFilters ? 'Probeer de laatste vier tekens van de code, of een andere status.' : 'Bonnen uit de webshop verschijnen hier vanzelf. Een bon voor een winactie maak je met "Nieuwe bon".'} action={hasFilters ? <button type="button" onClick={clearFilters} className={btnSecondary} data-id="element-5473" data-mp="2czphie6oec99">
                Filters wissen
              </button> : undefined} data-id="element-5472" data-mp="1u9g4e99cp02s" /> : page ? <>
          <ul ref={listRef} className={`${listBox} scroll-mt-24 ${fadeWhileLoading(status === 'loading')}`} data-id="element-5474" data-mp="2my4z9qjtc963">
            {page.items.map(v => {
          const badge = voucherStatusBadge[v.status];
          return <li key={v.id} data-id="element-5475" data-mp="sl5ms6aqbv31">
                  <Link to={`/be/cadeaubonnen/${v.id}`} state={{
              from: listQuery
            }} className={rowLink} data-id="element-5476" data-mp="slx8mpeiyeg4">
                    <span className="hidden w-24 shrink-0 sm:block" data-id="element-5477" data-mp="xks0egugzdcd">
                      <Badge tone={badge.tone} data-id="element-5478" data-mp="pird89oj5hqf">{badge.label}</Badge>
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-5479" data-mp="kkg1fteocx5k">
                      <span className="block truncate font-mono text-[14px] tracking-wide text-ink" data-id="element-5480" data-mp="zg5j5fctuo4h">···{v.codeLast4}</span>
                      <span className="block truncate text-[12px] text-muted" data-id="element-5481" data-mp="uh2l4ev9li60">
                        {v.buyerName ? `${v.buyerName} · ` : ''}
                        {sourceLabel[v.source]} · tot {untilLabel(v.validUntil)}
                      </span>
                    </span>
                    <span className="shrink-0 text-right" data-id="element-5482" data-mp="1i6n4uzz8sjgc">
                      <span className="block text-[14px] tabular-nums text-ink" data-id="element-5483" data-mp="1i6569wp3xt59">{money(v.balanceCents)}</span>
                      <span className="block text-[12px] tabular-nums text-muted" data-id="element-5484" data-mp="1da685hdz8wrn">van {money(v.initialCents)}</span>
                      <span className="mt-1 block sm:hidden" data-id="element-5485" data-mp="1n5zwdy126ep9">
                        <Badge tone={badge.tone} data-id="element-5486" data-mp="1n7ufr7wolyr8">{badge.label}</Badge>
                      </span>
                    </span>
                    <ChevronRightIcon className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-5487" data-mp="25sh5mfwfx42l" />
                  </Link>
                </li>;
        })}
          </ul>

          {page.pageCount > 1 && <nav aria-label="Pagina's" className="mt-4 flex items-center justify-between gap-3" data-id="element-5488" data-mp="21rx3sz0x9ca4">
              <button type="button" disabled={page.page <= 1} onClick={() => goToPage(page.page - 1)} className={`${btnSecondary} disabled:cursor-not-allowed disabled:opacity-40`} data-id="element-5489" data-mp="1n3k5hved3hcg">
                <ChevronLeftIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5490" data-mp="1e3nq6kdv3uu2" />
                Vorige
              </button>
              <span className="text-[13px] tabular-nums text-muted" data-id="element-5491" data-mp="1i5in7corfm21">
                Pagina {page.page} van {page.pageCount}
              </span>
              <button type="button" disabled={page.page >= page.pageCount} onClick={() => goToPage(page.page + 1)} className={`${btnSecondary} disabled:cursor-not-allowed disabled:opacity-40`} data-id="element-5492" data-mp="1941am4aka2i3">
                Volgende
                <ChevronRightIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5493" data-mp="1flcj6cwv1brt" />
              </button>
            </nav>}
        </> : null}

      {API_BASE === null && <p className="mt-8 text-[12px] text-muted" data-id="element-5494" data-mp="1aom863zwx6d9">Dit zijn voorbeeldbonnen uit de demo.</p>}
    </div>;
}